<template>
  <div class="fu-aw" :class="`fu-aw--${layout}`" data-test="fu-admin-worklist" :data-layout="layout">
    <div class="fu-aw__card">
      <div class="fu-aw__head">
        <div class="fu-aw__searchrow">
          <label class="fu-aw__search">
            <span class="fu-aw__sr">Search patient or carrier</span>
            <fu-icon name="search" :size="16" class="fu-aw__search-icon" />
            <input
              v-model="search"
              type="search"
              class="search-input fu-aw__search-input"
              placeholder="Search patient or carrier"
              autocomplete="off"
              data-test="fu-aw-search"
            />
          </label>
          <template v-if="query">
            <span class="fu-aw__matches" data-test="fu-aw-matches" aria-live="polite">
              <strong>{{ matchCount }}</strong> {{ matchCount === 1 ? 'open item matches' : 'open items match' }} “{{ query }}”
            </span>
            <button type="button" class="fu-aw__clear" data-test="fu-aw-clear" @click="search = ''">
              <fu-icon name="x" :size="14" :stroke="2.2" />Clear
            </button>
          </template>
        </div>
        <div class="fu-aw__chips" role="group" aria-label="Show">
          <button
            v-for="c in chips"
            :key="c.key"
            type="button"
            class="fu-aw__chip"
            :class="{ 'fu-aw__chip--on': c.key === activeChip }"
            :aria-pressed="c.key === activeChip ? 'true' : 'false'"
            data-test="fu-aw-chip"
            :data-chip="c.key"
            @click="setChip(c.key)"
          >
            {{ c.label }}<span class="fu-aw__chip-count" data-test="fu-aw-chip-count">{{ c.count }}</span>
          </button>
        </div>
      </div>

      <div
        v-if="layout !== 'phone' && (busy || sections.length)"
        class="fu-aw__colhead"
        :style="{ gridTemplateColumns: gridTemplate }"
        role="row"
        data-test="fu-aw-colhead"
      >
        <span v-for="c in columns" :key="c" role="columnheader" :data-col="c">{{ heads[c] || '' }}</span>
      </div>

      <followups-table
        v-if="busy"
        :columns="columns"
        :items="[]"
        :layout="layout"
        :today="todayStr"
        :busy="true"
        :show-head="false"
        :show-foot="false"
      />

      <template v-else>
        <section
          v-for="s in sections"
          :key="s.key"
          class="fu-aw__section"
          data-test="fu-aw-section"
          :data-section="s.key"
        >
          <div class="fu-aw__sechead">
            <span class="fu-aw__secdot" :class="`fu-aw__secdot--${s.key}`" aria-hidden="true"></span>
            <h3 class="fu-aw__sectitle">{{ s.title }}</h3>
            <span class="fu-aw__seccount" data-test="fu-aw-section-count">{{ s.count }}</span>
            <span class="fu-aw__sechint">{{ s.hint }}</span>
          </div>

          <template v-if="s.key === 'tasks'">
            <admin-task-row
              v-for="t in s.tasks"
              :key="t.id"
              :task="t"
              :grid-template="layout === 'desktop' && columns.length === 5 ? gridTemplate : null"
              :complete-delay="completeDelay"
              @open="task => $emit('open-task', task)"
              @complete="change => $emit('task-completed', change)"
            />
          </template>

          <followups-table
            v-else
            :columns="columns"
            :items="s.items"
            :total="s.count"
            :layout="layout"
            :today="todayStr"
            :date-format="dateFormatStr"
            :opening-ids="openingIds"
            :error-ids="errorIds"
            :popover-row-id="popover ? popover.id : null"
            :show-head="false"
            :show-foot="false"
            noun="follow-ups"
            @open-drawer="openDrawer"
            @open-followup="openPopover"
          >
            <template #cell-queue="{ item }">
              <span class="fu-aw-type" :class="`fu-aw-type--${item.queueKey}`" data-test="fu-aw-type" :data-queue="item.queueKey">
                <fu-icon :name="item.cells.queue.icon" :size="13" :stroke="2.1" />{{ item.cells.queue.label }}
              </span>
            </template>
            <template #cell-patient="{ item, opening, error }">
              <div class="fu-aw-patient" data-test="fu-cell-patient">
                <span class="fu-aw-patient__avatar" aria-hidden="true">{{ item.initials }}</span>
                <div class="fu-aw-patient__text">
                  <div class="fu-aw-patient__line">
                    <fu-icon v-if="opening" name="spin" :size="16" :stroke="2.4" class="fu-aw-patient__spin" label="Opening the follow-up panel" data-test="fu-opening" />
                    <span
                      v-if="error"
                      v-b-tooltip.hover.focus
                      class="fu-aw-patient__error"
                      tabindex="0"
                      role="img"
                      :title="openErrorText"
                      :aria-label="openErrorText"
                      data-test="fu-open-error"
                    >
                      <fu-icon name="alert" :size="16" />
                    </span>
                    <button type="button" class="fu-aw-patient__name" data-test="fu-open-drawer" @click="openDrawer(item)">
                      {{ item.name || 'Unnamed patient' }}
                    </button>
                    <span
                      v-if="item.list"
                      v-b-tooltip.hover.focus
                      class="fu-aw-patient__list"
                      tabindex="0"
                      role="img"
                      :title="listText(item)"
                      :aria-label="listText(item)"
                      data-test="fu-list-badge"
                    >
                      <fu-icon name="list" :size="14" />
                    </span>
                  </div>
                  <div class="fu-aw-patient__status" data-test="fu-aw-status">
                    <span class="fu-aw-dot" :class="`fu-aw-dot--${item.cells.status.category || 'none'}`" aria-hidden="true"></span>
                    <span class="fu-aw-patient__status-label" :title="item.statusLabel">{{ item.statusLabel }}</span>
                  </div>
                </div>
              </div>
            </template>
            <template #cell-detail="{ item }">
              <div class="fu-aw-detail" data-test="fu-aw-detail">
                <span v-if="item.cells.detail.muted" class="fu-aw-detail__muted">{{ item.cells.detail.muted }}</span>
                <template v-else>
                  <div :class="item.cells.detail.money ? 'fu-aw-detail__money' : 'fu-aw-detail__main'">{{ item.cells.detail.main }}</div>
                  <div v-if="item.cells.detail.sub" class="fu-aw-detail__sub">{{ item.cells.detail.sub }}</div>
                </template>
              </div>
            </template>
          </followups-table>

          <button
            v-if="s.more > 0"
            type="button"
            class="fu-aw__more"
            data-test="fu-aw-show-more"
            @click="pastDueExpanded = true"
          >
            Show {{ s.more }} more past due
            <fu-icon name="chev" :size="14" />
          </button>
        </section>

        <div v-if="!sections.length" class="fu-aw__empty" data-test="fu-aw-empty">{{ emptyText }}</div>
      </template>
    </div>

    <followup-popover
      v-if="popoverItem"
      :key="popover.id"
      :name="popoverItem.name"
      :eyebrow="[popoverQueue.label, popoverItem.name].filter(Boolean).join(' · ')"
      :fu="popoverItem.fu"
      :attempts="popoverItem.attempts.count"
      :status-label="popoverItem.statusLabel"
      :today="todayStr"
      :date-format="dateFormatStr"
      :anchor="popover.rect"
      :layout="layout"
      :show-assign="!!popoverQueue.showsAssignee"
      :employees="popoverEmployees"
      :initial-employee-id="popoverEmployeeId"
      :note-placeholder="notePlaceholder"
      @save="onPopoverSave"
      @remove-initial="runDanger('removeInitial')"
      @stop="runDanger('stopFollowups')"
      @restart="runDanger('restartFollowups')"
      @close="closePopover"
    />
  </div>
</template>

<script>
/**
 * AdminWorklist — the Admin "Today" page's merged worklist (Admin redesign Phase A1, 2026-10-06;
 * design "Today 3a", left column). A SIBLING of FollowupsQueue (one queue) — not its dashboard
 * mode: every row here keeps its OWN queue.
 *
 * Plan: C:\Users\nilay\.claude\plans\eager-painting-hejlsberg.md — "Phase A — A1".
 *
 *   <admin-worklist :open-items="split.open" :tasks="todaysTasksForEmployee(userID)"
 *     :visible-queue-keys="visibleQueueKeys" :sort="sort" :busy="busy" :today="d"
 *     @open-task="openTaskDetails" @saved="refetch" @call-goals-updated="refetch" />
 *
 * Props (the page owns the data, the scope and the sort; this owns search, chip, show-more and
 * the open popover):
 *   openItems         dashboard.js splitDashboardRows(adminData, …).open — { queueKey, row }, the
 *                     ROW objects themselves (saves patch them in place), already scope-filtered
 *   tasks             today's tasks for the user (tasks/todaysTasksForEmployee(userID))
 *   visibleQueueKeys  the queues the user may see (chips; rows of any other queue are ignored).
 *                     null ⇒ all six
 *   sort              dashboard.js SORT_KEYS: 'recent' | 'oldest' | 'amount'
 *   busy              skeleton rows instead of sections
 *   today             'YYYY-MM-DD' — the query's `d` (moment's default zone). null ⇒ own clock
 *   canEdit           null ⇒ $can('update', 'followupDate'), as FollowupsQueue
 *   completeDelay     AdminTaskRow's pause before saving a completed task (specs pass 0)
 *
 * Events:
 *   open-task(task)          a task's text was clicked — the page opens its task panel
 *   task-completed(change)   a task was completed (tasks/updateTask already dispatched)
 *   saved({ queueKey, row, action })  a popover save succeeded — the row is already patched in
 *                            place; the page may re-fetch to move it to the done rail
 *   call-goals-updated(i)    the follow-up drawer closed with a logged attempt (row patched
 *                            completed = 'true' — followupActions.js), as FollowupsQueue emits it
 *
 * Saves, popover and drawer: one followupActions.js instance PER QUEUE (host = this component,
 * seen through that queue: its queueDef, its open rows, mode 'dashboard' — the same store
 * actions, payloads and drawer query AdminTable sends from the old Admin page). The row id the
 * table uses is '<queueKey>:task-<TaskID>' (rowModel buildDashboardRow); the actions key their
 * saving / opening / error state by `actionId` (buildRow's id) and are mapped back here.
 *
 * ⛔ Never reads $route. ⛔ Never mounts a <slideout-panel> (the page owns the single outlet).
 * ⛔ No props named patNum / siteID / loading (src/mixin.js computeds would replace them).
 */
import moment from 'moment-timezone';
import mixin from '../../mixin.js';
import {
  queueByKey, columnsFor, layoutFor, tableMinWidth, COLUMN_DEFS, DASHBOARD_QUEUE,
} from '@/utils/followups/queues.js';
import { resolveFollowupState } from '@/utils/followups/followupState.js';
import { statusCategory } from '@/utils/followups/statusCategory.js';
import { buildDashboardRow } from '@/utils/followups/rowModel.js';
import {
  DASHBOARD_QUEUES, groupOpen, sortOpen, matchesWorklistSearch, matchesTaskSearch, chipCounts,
} from '@/utils/followups/dashboard.js';
import { cleanTaskContent } from '@/utils/tasks/completeTask.js';
import { createFollowupActions, OPEN_ERROR_TEXT } from './followupActions.js';
import FollowupsTable from './FollowupsTable.vue';
import FollowupPopover from './FollowupPopover.vue';
import AdminTaskRow from './AdminTaskRow.vue';
import FuIcon from './FuIcon.vue';

/** "Past due" shows this many rows, then "Show N more past due". */
const PAST_DUE_PAGE = 8;

const SORT_HINTS = { recent: 'Most recent first', oldest: 'Oldest first', amount: 'Largest amount first' };

const ALL_KEYS = DASHBOARD_QUEUES.map(q => q.key);

export default {
  name: 'AdminWorklist',
  components: { FollowupsTable, FollowupPopover, AdminTaskRow, FuIcon },
  mixins: [mixin],
  props: {
    openItems: { type: Array, default: () => [] },
    tasks: { type: Array, default: () => [] },
    visibleQueueKeys: { type: Array, default: null },
    sort: { type: String, default: 'recent' },
    busy: { type: Boolean, default: false },
    today: { type: String, default: null },
    canEdit: { type: Boolean, default: null },
    completeDelay: { type: Number, default: 1200 },
  },

  data() {
    return {
      search: '',
      chip: 'all',
      pastDueExpanded: false,
      containerWidth: 1280,
      popover: null,
      clockToday: moment().format('YYYY-MM-DD'),
      heads: DASHBOARD_QUEUE.heads,
      openErrorText: OPEN_ERROR_TEXT,
    };
  },

  computed: {
    todayStr() {
      return this.today || this.clockToday;
    },

    canEditResolved() {
      if (this.canEdit === null || this.canEdit === undefined) {
        return !!(this.$can && this.$can('update', 'followupDate'));
      }
      return this.canEdit === true;
    },

    /**
     * Chosen by FIT on this component's own width, not by the export's 900px breakpoint: the
     * worklist shares the row with the done rail, so in a 1280 window (app sidebar + rail) the card
     * is ~890px — under 900, yet the five desktop columns need only ~820px. The 900 rule drew the
     * tablet set there and lost Attempts (2026-10-06). So: below the phone breakpoint → cards; else
     * the desktop set if it fits, else the tablet set if it fits, else cards — never sideways scroll.
     */
    layout() {
      const w = this.containerWidth;
      if (layoutFor(w) === 'phone') return 'phone';
      const room = w - 2; // the card's 1px border each side
      if (tableMinWidth(DASHBOARD_QUEUE.columns.desktop) <= room) return 'desktop';
      if (tableMinWidth(DASHBOARD_QUEUE.columns.tablet) <= room) return 'tablet';
      return 'phone';
    },

    columns() {
      return DASHBOARD_QUEUE.columns[this.layout].slice();
    },

    gridTemplate() {
      return this.columns.map(c => (COLUMN_DEFS[c] ? COLUMN_DEFS[c].width : 'minmax(0,1fr)')).join(' ');
    },

    currencySymbol() {
      const s = this.allSettings && this.allSettings.GENERAL_CURRENCY_SYMBOL;
      return s && s.PropertyValue ? s.PropertyValue : '$';
    },

    notePlaceholder() {
      const s = this.allSettings && this.allSettings.GENERAL_FOLLOWUP_NOTE_COPY_TO_COMMLOG;
      return s && s.PropertyValue === 'TRUE'
        ? 'Optional. Add notes here. It will save to commlog...'
        : 'Optional. Add notes here...';
    },

    visibleKeys() {
      return Array.isArray(this.visibleQueueKeys) ? this.visibleQueueKeys : ALL_KEYS;
    },

    query() {
      return this.search.trim();
    },

    /** The page's open items, of the visible queues only. */
    scopedItems() {
      return (this.openItems || []).filter(it => it && it.row && this.visibleKeys.indexOf(it.queueKey) !== -1);
    },

    /** One view-model per open item, WITHOUT the saving overlay. Keyed by the item object. */
    baseModels() {
      const ctxBase = {
        siteID: this.siteID,
        today: this.todayStr,
        dateFormat: this.dateFormatStr,
        currency: this.currencySymbol,
        categoryOf: statusCategory,
      };
      const map = new Map();
      this.scopedItems.forEach((it) => {
        const q = queueByKey(it.queueKey);
        if (!q) return;
        const fu = resolveFollowupState(it.row, { today: this.todayStr, canEdit: this.canEditResolved });
        const vm = buildDashboardRow(q, it.row, Object.assign({ fu }, ctxBase));
        vm.fuBase = fu;
        map.set(it, vm);
      });
      return map;
    },

    filteredItems() {
      return this.scopedItems.filter(it => this.baseModels.has(it) && matchesWorklistSearch(it.row, this.query));
    },

    taskModels() {
      const doc = typeof document !== 'undefined' ? document : null;
      return (this.tasks || []).filter(Boolean).map((task) => {
        const c = cleanTaskContent(task.content, doc);
        return { task, text: c.text, patientNames: c.patients.map(p => p.name) };
      });
    },

    filteredTasks() {
      return this.taskModels
        .filter(m => matchesTaskSearch(m.text, m.patientNames, this.query))
        .map(m => m.task);
    },

    chips() {
      return chipCounts(this.filteredItems, this.filteredTasks.length, this.visibleKeys);
    },

    activeChip() {
      return this.chips.some(c => c.key === this.chip) ? this.chip : 'all';
    },

    pool() {
      if (this.activeChip === 'all') return this.filteredItems;
      if (this.activeChip === 'tasks') return [];
      return this.filteredItems.filter(it => it.queueKey === this.activeChip);
    },

    grouped() {
      const g = groupOpen(this.pool);
      return { dueToday: sortOpen(g.dueToday, this.sort), pastDue: sortOpen(g.pastDue, this.sort) };
    },

    sections() {
      const out = [];
      const showTasks = this.activeChip === 'all' || this.activeChip === 'tasks';
      if (showTasks && this.filteredTasks.length) {
        out.push({ key: 'tasks', title: 'Your tasks', count: this.filteredTasks.length, hint: 'Assigned to you, due today', tasks: this.filteredTasks, items: [], more: 0 });
      }
      if (this.grouped.dueToday.length) {
        out.push({ key: 'dueToday', title: 'Due today', count: this.grouped.dueToday.length, hint: 'Scheduled for today', tasks: [], items: this.grouped.dueToday.map(this.modelOf), more: 0 });
      }
      const past = this.grouped.pastDue;
      if (past.length) {
        const shown = this.pastDueExpanded ? past : past.slice(0, PAST_DUE_PAGE);
        out.push({ key: 'pastDue', title: 'Past due', count: past.length, hint: SORT_HINTS[this.sort] || SORT_HINTS.recent, tasks: [], items: shown.map(this.modelOf), more: past.length - shown.length });
      }
      return out;
    },

    matchCount() {
      return this.filteredItems.length + this.filteredTasks.length;
    },

    emptyText() {
      return this.query
        ? 'No open items match. Try another name or carrier.'
        : 'Nothing left here. Everything is in the done column.';
    },

    shownModels() {
      const out = [];
      this.sections.forEach(s => s.items.forEach(vm => out.push(vm)));
      return out;
    },

    // The actions' per-queue state (keyed by actionId), mapped onto the table's row ids.
    openingIds() {
      return this.flagMap('opening');
    },

    errorIds() {
      return this.flagMap('errors');
    },

    popoverItem() {
      if (!this.popover) return null;
      for (const it of this.baseModels.keys()) {
        const vm = this.baseModels.get(it);
        if (vm.id === this.popover.id) return this.withSaving(vm);
      }
      return null;
    },

    popoverQueue() {
      return (this.popoverItem && queueByKey(this.popoverItem.queueKey)) || {};
    },

    popoverEmployees() {
      if (!this.popoverItem) return [];
      return this.actionsFor(this.popoverItem.queueKey).responsibleEmployees(this.popoverItem.row);
    },

    popoverEmployeeId() {
      if (!this.popoverItem) return '';
      const e = this.actionsFor(this.popoverItem.queueKey).preselectedEmployee(this.popoverItem.row);
      return e ? String(e.EmployeeID) : '';
    },
  },

  watch: {
    search() { this.pastDueExpanded = false; },
    chip() { this.pastDueExpanded = false; },
    sort() { this.pastDueExpanded = false; },
    // The row under an open popover went away (moved to done, filtered out, reloaded) — close it.
    popoverItem(v) { if (!v && this.popover) this.closePopover(); },
  },

  created() {
    // One followupActions per queue, each seeing this component through that queue.
    this.actionsByQueue = {};
    ALL_KEYS.forEach((key) => {
      this.actionsByQueue[key] = createFollowupActions(this.hostFor(key));
    });
    // The lock cleanup (beforeunload / tab hidden) is global — one listener set is enough.
    this.actionsByQueue[ALL_KEYS[0]].install();
  },

  mounted() {
    this.measure();
    if (typeof window.ResizeObserver === 'function') {
      // Applied on the next frame, as FollowupsQueue does (no "ResizeObserver loop" warning when a
      // breakpoint swaps the layout inside the callback).
      this.resizeObserver = new window.ResizeObserver((entries) => {
        const e = entries && entries[0];
        const w = e && e.contentRect ? e.contentRect.width : 0;
        if (typeof window.requestAnimationFrame !== 'function') { this.applyWidth(w); return; }
        if (this.resizeFrame) window.cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = window.requestAnimationFrame(() => { this.resizeFrame = null; this.applyWidth(w); });
      });
      this.resizeObserver.observe(this.$el);
    } else {
      window.addEventListener('resize', this.measure);
    }
    this.clock = window.setInterval(this.tick, 60000);
  },

  beforeDestroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.resizeFrame && typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(this.resizeFrame);
    window.removeEventListener('resize', this.measure);
    window.clearInterval(this.clock);
    this.closePopover();
    this.actionsByQueue[ALL_KEYS[0]].dispose();
  },

  methods: {
    /**
     * followupActions.js's host for one queue: this component's store / panel / toast / user /
     * settings, that queue's definition and open rows, mode 'dashboard' (no list id — what
     * AdminTable sends on the old Admin page).
     */
    hostFor(key) {
      const vm = this;
      const queueDef = queueByKey(key);
      return {
        get $store() { return vm.$store; },
        get $showPanel() { return vm.$showPanel; },
        get $toast() { return vm.$toast; },
        $can: (...args) => !!(vm.$can && vm.$can(...args)),
        $emit: (...args) => vm.$emit(...args),
        get siteID() { return vm.siteID; },
        get userID() { return vm.userID; },
        get user() { return vm.user; },
        get allSettings() { return vm.allSettings; },
        get activeEmployees() { return vm.activeEmployees; },
        get dateFormatStr() { return vm.dateFormatStr; },
        queueDef,
        get rows() { return vm.scopedItems.filter(it => it.queueKey === key).map(it => it.row); },
        listId: undefined,
        mode: 'dashboard',
        profilePatientId: null,
      };
    },

    actionsFor(key) {
      return this.actionsByQueue[key];
    },

    /** The view-model with the "Saving…" follow-up state laid over it while its save runs. */
    withSaving(vm) {
      const a = this.actionsFor(vm.queueKey);
      if (!a || !vm.actionId || !a.state.saving[vm.actionId]) return vm;
      const fu = resolveFollowupState(vm.row, { today: this.todayStr, canEdit: this.canEditResolved, saving: true });
      return Object.assign({}, vm, { fu });
    },

    modelOf(item) {
      return this.withSaving(this.baseModels.get(item));
    },

    flagMap(kind) {
      const out = {};
      this.shownModels.forEach((vm) => {
        const a = this.actionsFor(vm.queueKey);
        if (a && vm.actionId && a.state[kind][vm.actionId]) out[vm.id] = true;
      });
      return out;
    },

    listText(item) {
      const name = item.list && item.list.name;
      return name ? `Belongs to list - ${name}` : 'Belongs to a list';
    },

    setChip(key) {
      this.chip = key;
    },

    tick() {
      this.clockToday = moment().format('YYYY-MM-DD');
    },

    measure() {
      const el = this.$el;
      const w = el ? (el.getBoundingClientRect().width || el.clientWidth) : 0;
      this.applyWidth(w || window.innerWidth);
    },

    applyWidth(w) {
      const n = Math.round(Number(w));
      if (Number.isFinite(n) && n > 0) this.containerWidth = n;
    },

    openDrawer(item) {
      if (!item) return;
      this.closePopover();
      this.actionsFor(item.queueKey).openDrawer(item.row);
    },

    openPopover({ item, el }) {
      if (!item || !item.id || !item.fu) return;
      if (!item.fu.editable && !item.fu.canRestart) return;
      if (this.popover && this.popover.id === item.id) {
        this.closePopover();
        return;
      }
      const r = el && el.getBoundingClientRect ? el.getBoundingClientRect() : null;
      this.popoverAnchorEl = el || null;
      this.popover = {
        id: item.id,
        rect: r ? { top: r.top, bottom: r.bottom, left: r.left, right: r.right, width: r.width, height: r.height } : null,
      };
    },

    closePopover() {
      if (!this.popover) return;
      this.popover = null;
      const el = this.popoverAnchorEl;
      this.popoverAnchorEl = null;
      if (el && typeof el.focus === 'function' && document.body.contains(el)) {
        this.$nextTick(() => el.focus());
      }
    },

    async onPopoverSave({ kind, date, note, employeeId }) {
      const item = this.popoverItem;
      if (!item) return;
      const actions = this.actionsFor(item.queueKey);
      const employee = employeeId === '' || employeeId === null || employeeId === undefined
        ? null
        : (actions.responsibleEmployees(item.row).find(e => String(e.EmployeeID) === String(employeeId)) || null);
      this.closePopover();
      const action = kind === 'initial' ? 'setInitialDate' : 'changeDate';
      const ok = kind === 'initial'
        ? await actions.setInitialDate(item.row, { date, note, employee })
        : await actions.changeDate(item.row, { date, employee });
      if (ok) this.$emit('saved', { queueKey: item.queueKey, row: item.row, action });
    },

    async runDanger(action) {
      const item = this.popoverItem;
      if (!item) return;
      this.closePopover();
      const ok = await this.actionsFor(item.queueKey)[action](item.row);
      if (ok) this.$emit('saved', { queueKey: item.queueKey, row: item.row, action });
    },
  },
};
</script>

<style lang="scss" scoped>
@import './mixins';

.fu-aw {
  @include fu-font-base;
  position: relative;
  min-width: 0;
}

.fu-aw__card {
  @include fu-card;
  overflow: hidden;
}

.fu-aw__head {
  display: flex;
  flex-direction: column;
  gap: $fu-space-3;
  padding: 14px 18px;
  border-bottom: 1px solid $fu-border;
}

.fu-aw__searchrow {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: $fu-space-3;
}

.fu-aw__sr {
  @include fu-visually-hidden;
}

.fu-aw__search {
  position: relative;
  flex: 0 1 340px;
  min-width: 0;
  margin: 0;
}

.fu-aw__search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: $fu-text-muted;
  pointer-events: none;
}

.fu-aw__search-input {
  @include fu-input;
  padding-left: 36px;
  &::-webkit-search-cancel-button { -webkit-appearance: none; }
}

.fu-aw__matches {
  font-size: 13px;
  color: $fu-neutral-600;
  overflow-wrap: anywhere;
  strong { font-weight: $fu-fw-bold; color: $fu-text; }
}

.fu-aw__clear {
  @include fu-button-reset;
  @include fu-focus-ring;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: $fu-radius-xs;
  font-size: 13px;
  font-weight: $fu-fw-bold;
  color: $fu-brand-700;
  &:hover { text-decoration: underline; }
}

.fu-aw__chips {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: $fu-space-2;
}

.fu-aw__chip {
  @include fu-chip;
}

.fu-aw__chip--on {
  @include fu-chip(true);
}

.fu-aw__chip-count {
  @include fu-tabular;
  opacity: 0.65;
}

.fu-aw__colhead {
  display: grid;
  gap: $fu-space-3;
  align-items: center;
  padding: 9px 18px;
  background: $fu-neutral-25;
  border-bottom: 1px solid $fu-border;
  @include fu-eyebrow;
  span { min-width: 0; white-space: nowrap; }
}

.fu-aw__sechead {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 10px;
  padding: $fu-space-4 18px $fu-space-2;
}

.fu-aw__secdot {
  @include fu-status-dot($fu-neutral-700);
  &--dueToday { background: $fu-brand; }
  &--pastDue { background: $fu-danger; }
}

.fu-aw__sectitle {
  margin: 0;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: $fu-text;
}

.fu-aw__seccount {
  @include fu-pill;
  @include fu-tabular;
  min-width: 22px;
  justify-content: center;
}

.fu-aw__sechint {
  font-size: 12.5px;
  color: $fu-text-muted;
}

.fu-aw__more {
  @include fu-button-reset;
  @include fu-focus-ring;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: $fu-space-3 18px;
  border-bottom: 1px solid $fu-border;
  font-size: 13px;
  font-weight: $fu-fw-bold;
  color: $fu-brand-700;
  &:hover { background: $fu-neutral-25; }
}

.fu-aw__empty {
  padding: 40px 18px;
  text-align: center;
  font-size: 14px;
  color: $fu-text-muted;
}

// ---- cells drawn through FollowupsTable's cell-<column> slots ---------------------------------
.fu-aw-type {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 24px;
  max-width: 100%;
  padding: 0 9px 0 7px;
  border-radius: $fu-radius-pill;
  border: 1px solid $fu-brand-200;
  background: $fu-brand-50;
  color: $fu-brand-700;
  font-size: 12px;
  font-weight: $fu-fw-bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  &--unscheduledTreatment { border-color: mix($fu-cat-claim, #ffffff, 30%); background: $fu-cat-claim-subtle; color: darken($fu-cat-claim, 22%); }
  &--brokenAppointments { border-color: mix($fu-warning, #ffffff, 35%); background: $fu-warning-subtle; color: $fu-warning-text; }
  &--outstandingClaims,
  &--outstandingPreAuths { border-color: $fu-border-strong; background: $fu-neutral-50; color: $fu-neutral-700; }
  &--patientBalances { border-color: mix($fu-success, #ffffff, 30%); background: $fu-success-subtle; color: $fu-success; }
}

.fu-aw-patient {
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 0;
}

.fu-aw-patient__avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: $fu-brand-100;
  color: $fu-brand-700;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: $fu-fw-bold;
  font-size: 11.5px;
  flex: none;
}

.fu-aw-patient__text {
  min-width: 0;
}

.fu-aw-patient__line {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.fu-aw-patient__name {
  @include fu-button-reset;
  @include fu-ellipsis;
  @include fu-focus-ring;
  min-width: 0;
  max-width: 100%;
  border-radius: $fu-radius-xs;
  font-weight: $fu-fw-bold;
  font-size: 14px;
  color: $fu-link;
  &:hover { text-decoration: underline; }
}

.fu-aw-patient__spin {
  flex: none;
  color: $fu-brand;
  @include fu-spin;
}

.fu-aw-patient__error {
  display: inline-flex;
  flex: none;
  color: $fu-danger;
  border-radius: 50%;
  @include fu-focus-ring;
}

.fu-aw-patient__list {
  display: inline-flex;
  flex: none;
  color: $fu-brand-500;
  border-radius: $fu-radius-xs;
  @include fu-focus-ring;
}

.fu-aw-patient__status {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 3px;
  min-width: 0;
  font-size: 13px;
  color: $fu-text;
}

.fu-aw-patient__status-label {
  @include fu-ellipsis;
  min-width: 0;
}

.fu-aw-dot {
  @include fu-status-dot;
  @include fu-status-dot-colors;
}

.fu-aw-detail {
  min-width: 0;
}

.fu-aw-detail__main {
  @include fu-ellipsis;
  font-size: 13.5px;
  font-weight: $fu-fw-semibold;
}

.fu-aw-detail__money {
  @include fu-font-display;
  @include fu-tabular;
  font-weight: 800;
  font-size: 15px;
}

.fu-aw-detail__sub {
  @include fu-ellipsis;
  font-size: 12px;
  color: $fu-text-muted;
  margin-top: 2px;
}

.fu-aw-detail__muted {
  font-size: 13px;
  color: $fu-text-subtle;
}

// ---- phone: cards, full-width search ---------------------------------------------------------
.fu-aw--phone {
  .fu-aw__head { padding: 14px $fu-space-4; }
  .fu-aw__search { flex: 1 1 100%; }
  .fu-aw__search-input { font-size: 16px; }
  .fu-aw__sechead { padding: 14px $fu-space-4 $fu-space-2; }
  .fu-aw-detail__main,
  .fu-aw-detail__sub { white-space: normal; overflow-wrap: anywhere; }
}
</style>
